import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from '../lib/auth-context';
import { Flow } from '../types/Document';
import { FASTAPI_URL } from '@/lib/config';
import { toast } from '@/hooks/use-toast';

export interface UseSiteflowIntegrationReturn {
  sendingToSiteflow: boolean;
  message: string;
  error: string;
  handleSendToSiteflow: (
    processingResults: Flow[] | any,
    isFlowArray: boolean,
    projectId: string,
    familyId: string
  ) => Promise<void>;
  clearMessages: () => void;
}

export const useSiteflowIntegration = (): UseSiteflowIntegrationReturn => {
  const { user } = useAuth();
  const [sendingToSiteflow, setSendingToSiteflow] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const clearMessages = () => {
    setMessage('');
    setError('');
  };

  const handleSendToSiteflow = useCallback(
    async (
      processingResults: Flow[] | any,
      isFlowArray: boolean,
      projectId: string,
      familyId: string
    ) => {
      if (!user?.token || !processingResults || !isFlowArray) {
        setError('Cannot send to Siteflow: Missing token, results, or results are not in the correct format.');
        return;
      }

      if (!projectId || !familyId) {
        setError('Cannot send to Siteflow: Project and Family must be selected.');
        return;
      }

      setSendingToSiteflow(true);
      setError('');
      setMessage('');

      try {
        const flowDataToSend = processingResults[0];
        const siteflowEndpoint = `${FASTAPI_URL}/siteflow/create_flow`;

        const response = await fetch(siteflowEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.token}`,
          },
          body: JSON.stringify({
            flowData: flowDataToSend,
            projectId,
            familyId,
          }),
        });

        if (!response.ok) {
          const text = await response.text();
          let detail: string;
          try {
            detail = JSON.parse(text).detail;
          } catch {
            const statusText = response.statusText || 'Unknown error';
            detail = `HTTP ${response.status}: ${statusText}`;
          }
          throw new Error(detail || 'Failed to send data to Siteflow');
        }

        const responseData = await response.json();
        const successMsg = `Successfully sent to Siteflow. Flow ID: ${responseData.siteflow_flow_id}`;
        setMessage(successMsg);
        toast({ title: 'Sent to Siteflow', description: successMsg });
        setTimeout(() => setMessage(''), 5000);
      } catch (err) {
        Sentry.captureException(err, {
          tags: { flow: 'send-to-siteflow' },
          extra: { projectId, familyId },
        });
        console.error('Error sending to Siteflow:', err);
        const errorMsg = err instanceof Error ? err.message : 'Error sending data to Siteflow';
        setError(errorMsg);
        toast({ variant: 'destructive', title: 'Siteflow Error', description: errorMsg });
      } finally {
        setSendingToSiteflow(false);
      }
    },
    [user?.token]
  );

  return {
    sendingToSiteflow,
    message,
    error,
    handleSendToSiteflow,
    clearMessages,
  };
};
